import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RotateCcwIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Case, SortConfig } from '../types';
import { MOCK_CASES } from '../mockData';
import { SkeletonTable } from './SkeletonTable';
import { EmptyState } from './EmptyState';
import { ActionBar } from './ActionBar';
import { CasesTable } from './CasesTable';
import { ConfirmDialog } from './ConfirmDialog';
export function RestoreDeletedCases() {
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: 'deletedOn',
    direction: 'desc'
  });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  // Simulate initial fetch
  const fetchCases = useCallback(async (showRefreshState = false) => {
    if (showRefreshState) setIsRefreshing(true);else setIsLoading(true);
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, showRefreshState ? 800 : 1500));
    setCases([...MOCK_CASES]);
    setSelectedIds(new Set());
    if (showRefreshState) setIsRefreshing(false);else setIsLoading(false);
  }, []);
  useEffect(() => {
    fetchCases();
  }, [fetchCases]);
  // Filter and sort logic
  const processedCases = useMemo(() => {
    // 1. Filter
    let result = cases;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => c.subject.toLowerCase().includes(query) || c.description.toLowerCase().includes(query));
    }
    // 2. Sort
    if (sortConfig) {
      result = [...result].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [cases, searchQuery, sortConfig]);
  // Handlers
  const handleSort = (key: keyof Case) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return {
          key,
          direction: current.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      return {
        key,
        direction: 'desc'
      }; // Default to desc for dates
    });
  };
  const handleToggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };
  const handleToggleSelectAll = () => {
    if (selectedIds.size === processedCases.length && processedCases.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(processedCases.map(c => c.id)));
    }
  };
  const handleRestoreConfirm = async () => {
    setIsRestoring(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1200));
    // Simulate 5% chance of error for realism, but mostly succeed
    const isError = Math.random() > 0.95;
    if (isError) {
      toast.error('Failed to restore one or more Cases. Please try again.', {
        icon: <XCircleIcon className="w-5 h-5 text-red-500" data-id="element-84" />,
        className: 'border-red-200 bg-red-50 text-red-900'
      });
    } else {
      const restoreCount = selectedIds.size;
      // Remove restored cases from local state
      setCases(current => current.filter(c => !selectedIds.has(c.id)));
      setSelectedIds(new Set());
      toast.success(`${restoreCount} ${restoreCount === 1 ? 'Case was' : 'Cases were'} restored successfully.`, {
        icon: <CheckCircleIcon className="w-5 h-5 text-lime-600" data-id="element-85" />,
        className: 'border-lime-200 bg-lime-50 text-lime-900',
        duration: 4000
      });
    }
    setIsRestoring(false);
    setIsConfirmOpen(false);
  };
  return <div className="w-full max-w-6xl mx-auto bg-white rounded-xl shadow-lg shadow-slate-200/60 border border-slate-200 overflow-hidden flex flex-col" data-id="element-86">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-teal-600 px-5 py-4" data-id="element-87">
        <div className="flex items-center gap-3" data-id="element-88">
          <div className="w-9 h-9 bg-white/15 rounded-lg flex items-center justify-center backdrop-blur-sm" data-id="element-89">
            <RotateCcwIcon className="w-[18px] h-[18px] text-white" data-id="element-90" />
          </div>
          <div data-id="element-91">
            <h1 className="text-lg font-bold text-white tracking-tight leading-tight" data-id="element-92">
              Restore Deleted Cases
            </h1>
            <p className="text-cyan-100 text-xs mt-0.5" data-id="element-93">
              Review deleted Cases related to this Opportunity and select
              records to restore.
            </p>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex flex-col min-h-[400px]" data-id="element-94">
        {isLoading ? <SkeletonTable data-id="element-95" /> : cases.length === 0 ? <EmptyState data-id="element-96" /> : <>
            <ActionBar selectedCount={selectedIds.size} onRestoreClick={() => setIsConfirmOpen(true)} onRefresh={() => fetchCases(true)} searchQuery={searchQuery} onSearchChange={setSearchQuery} isRefreshing={isRefreshing} data-id="element-97" />

            {processedCases.length === 0 ? <div className="flex flex-col items-center justify-center py-16 text-center" data-id="element-98">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mb-3 border border-slate-200" data-id="element-99">
                  <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-100">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" data-id="element-101" />
                  </svg>
                </div>
                <p className="text-sm font-medium text-slate-700 mb-0.5" data-id="element-102">
                  No matching cases
                </p>
                <p className="text-xs text-slate-400" data-id="element-103">
                  Try adjusting your search terms.
                </p>
              </div> : <CasesTable cases={processedCases} selectedIds={selectedIds} onToggleSelect={handleToggleSelect} onToggleSelectAll={handleToggleSelectAll} sortConfig={sortConfig} onSort={handleSort} data-id="element-104" />}
          </>}
      </div>

      {/* Confirmation Modal */}
      <ConfirmDialog isOpen={isConfirmOpen} onClose={() => !isRestoring && setIsConfirmOpen(false)} onConfirm={handleRestoreConfirm} count={selectedIds.size} isRestoring={isRestoring} data-id="element-105" />
    </div>;
}